import { Button } from '../../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Loader2, Send } from 'lucide-react';
import type { SupplierUploadTracking } from '../../../lib/api/barratt';
import UploadStatusBadge from '../UploadStatusBadge';
import { datasetLabel, formatDay, formatDayTime } from '../uploadCycle';
import { UploadTableSkeleton, UploadTableEmpty } from './UploadTableStates';

interface NotUploadedTableProps {
  rows: SupplierUploadTracking[];
  loading: boolean;
  emptyMessage: string;
  onChase: (row: SupplierUploadTracking) => void;
  chasingIds: number[];
  /** Disables every per-row button while a bulk chase runs. */
  chasingAll: boolean;
}

const COLUMNS = 7;

/** "Suppliers did not upload": pending and overdue rows with a reminder action each. */
export default function NotUploadedTable({
  rows,
  loading,
  emptyMessage,
  onChase,
  chasingIds,
  chasingAll,
}: NotUploadedTableProps) {
  return (
    <Table data-testid="not-uploaded-table">
      <TableHeader>
        <TableRow>
          <TableHead>Supplier</TableHead>
          <TableHead>Dataset</TableHead>
          <TableHead>Contact email</TableHead>
          <TableHead>Due</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Last reminder</TableHead>
          <TableHead className="text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      {loading ? (
        <UploadTableSkeleton columns={COLUMNS} />
      ) : rows.length === 0 ? (
        <UploadTableEmpty columns={COLUMNS} message={emptyMessage} />
      ) : (
        <TableBody>
          {rows.map((row) => {
            const chasing = chasingIds.includes(row.id);
            const noEmail = !row.supplier_email;
            return (
              <TableRow key={row.id} className={row.status === 'overdue' ? 'bg-red-50/40' : undefined}>
                <TableCell className="font-medium">
                  {row.supplier_name || `Supplier ${row.supplier}`}
                  {row.supplier_contact_name && (
                    <p className="text-xs font-normal text-muted-foreground">{row.supplier_contact_name}</p>
                  )}
                </TableCell>
                <TableCell>{datasetLabel(row)}</TableCell>
                <TableCell>
                  {row.supplier_email ? (
                    <a href={`mailto:${row.supplier_email}`} className="text-primary hover:underline">
                      {row.supplier_email}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">No email on file</span>
                  )}
                </TableCell>
                <TableCell>{formatDay(row.due_date)}</TableCell>
                <TableCell>
                  <UploadStatusBadge status={row.status} label={row.status_display} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {row.last_reminder_at ? (
                    <span title={`${row.reminder_count} reminder${row.reminder_count === 1 ? '' : 's'} sent`}>
                      {formatDayTime(row.last_reminder_at)}
                      {row.reminder_count > 1 && (
                        <span className="ml-1 text-xs">(×{row.reminder_count})</span>
                      )}
                    </span>
                  ) : (
                    'Never'
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onChase(row)}
                    disabled={chasing || chasingAll || noEmail}
                    title={noEmail ? 'No contact email on file for this supplier' : undefined}
                  >
                    {chasing ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-1" />
                    )}
                    Send reminder
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      )}
    </Table>
  );
}
